import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { FadeUp } from "@/components/site/Reveal";
import { COMPANY, POLICIES, MOA_AOA, MATERIAL_DOCS } from "@/lib/assets";
import { FileText, TrendingUp, Scale, Users, Mail, ArrowUpRight, Download, FileCheck2, BookOpen } from "lucide-react";

const CORPORATE = [
  { label: "Company Name", value: COMPANY.name },
  { label: "CIN", value: COMPANY.cin },
  { label: "Type", value: "Public Limited Company" },
  { label: "Incorporated", value: "2024, Under the Companies Act, 2013" },
  { label: "Registered Office", value: COMPANY.address },
  { label: "Registrar of Companies", value: "RoC — Mumbai, Maharashtra" },
];

const useHashScroll = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const id = hash.slice(1);
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
  }, [hash]);
};

const Placeholder = ({ label = "Coming soon" }) => (
  <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border border-border text-muted-foreground">{label}</span>
);

const SectionHead = ({ eyebrow, title, right }) => (
  <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
    <FadeUp>
      <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary mb-4">/ {eyebrow}</p>
      <h2 className="font-display text-3xl md:text-5xl leading-[1.05]">{title}</h2>
    </FadeUp>
    {right && <div className="max-w-sm text-sm text-muted-foreground">{right}</div>}
  </div>
);

export default function Investors() {
  useHashScroll();
  return (
    <div className="bg-background" data-testid="investors-page">
      <Navbar />

      {/* Hero */}
      <header className="pt-40 md:pt-52 pb-16 border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <FadeUp>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary mb-6">/ Investor Relations</p>
            <h1 className="font-display text-6xl md:text-8xl leading-[0.9] max-w-4xl">
              Transparency,<br /> at every <span className="italic text-primary">ply.</span>
            </h1>
            <p className="text-muted-foreground max-w-xl mt-8 text-lg leading-relaxed">
              Corporate information, statutory disclosures and shareholder resources for {COMPANY.short}.
            </p>
          </FadeUp>
        </div>
      </header>

      {/* Corporate info (always visible) */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Corporate Information" title="Company at a glance" />
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border" data-testid="corporate-info">
            {CORPORATE.map((c) => (
              <div key={c.label} className="bg-card p-6 md:p-8">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">{c.label}</p>
                <p className="text-lg leading-snug">{c.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IPO */}
      <section id="ipo" className="bg-muted border-y border-border py-20 md:py-24 scroll-mt-24" data-testid="section-ipo">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="IPO" title="Initial Public Offering" right={<>Draft offer documents filed with SEBI and the Stock Exchanges are made available here for public review, in accordance with SEBI (ICDR) Regulations, 2018.</>} />
          <FadeUp>
            <div className="bg-background border border-border p-8 md:p-10 mb-6">
              <div className="flex items-start gap-4">
                <TrendingUp size={22} className="text-primary shrink-0 mt-1" strokeWidth={1.5} />
                <div className="flex-1">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-primary mb-2">Proposed Offering</p>
                  <h3 className="font-display text-2xl md:text-3xl">Initial Public Offer — Paptech Corp Limited</h3>
                  <p className="text-muted-foreground mt-3 max-w-2xl">Fresh issue of up to 40,00,000 equity shares of face value ₹10 each through the book-building process, in accordance with SEBI (ICDR) Regulations, 2018. The Draft Red Herring Prospectus and Draft Abridged Prospectus are filed for public comments.</p>
                </div>
              </div>
            </div>
          </FadeUp>
          <div className="grid md:grid-cols-2 gap-6" data-testid="ipo-documents">
            {[
              { title: "Draft Red Herring Prospectus (DRHP)", note: "Filed with SEBI & Stock Exchanges — for public comments", href: "/investors/ipo/Draft_Red_Herring_Prospectus.pdf", tag: "DRHP" },
              { title: "Draft Abridged Prospectus", note: "Summary of the Draft Red Herring Prospectus", href: "/investors/ipo/Draft_Abridged_Prospectus.pdf", tag: "Abridged" },
            ].map((it, i) => (
              <FadeUp key={it.title} delay={i * 0.08}>
                <a href={it.href} target="_blank" rel="noreferrer" className="bg-background border border-border p-8 flex items-start justify-between gap-4 group hover:bg-background/70 transition-colors h-full" data-testid={`ipo-doc-${i}`}>
                  <div className="flex gap-4 items-start">
                    <FileCheck2 size={22} className="text-primary shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-primary mb-2">{it.tag}</p>
                      <h3 className="font-display text-2xl group-hover:text-primary transition-colors leading-snug">{it.title}</h3>
                      <p className="text-muted-foreground mt-2 max-w-md">{it.note}</p>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-primary mt-4 inline-flex items-center gap-1.5">
                        Download PDF <ArrowUpRight size={12} />
                      </p>
                    </div>
                  </div>
                  <Download size={16} className="text-muted-foreground shrink-0 mt-1" />
                </a>
              </FadeUp>
            ))}
          </div>
          <FadeUp>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-8 max-w-3xl">Disclaimer — The Draft Red Herring Prospectus is a draft document and does not constitute an invitation or offer to subscribe to or purchase any securities. Investors are advised to refer to the Red Herring Prospectus / Prospectus, when filed, for the final terms of the offering.</p>
          </FadeUp>
        </div>
      </section>

      {/* Annual Report */}
      <section id="annual-report" className="py-20 md:py-24 scroll-mt-24" data-testid="section-annual-report">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Annual Report" title="Annual reports" />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { period: "FY 2024–25", href: "/investors/annual/Annual_Report_FY24-25.pdf" },
              { period: "FY 2025–26", href: "/investors/annual/Annual_Report_FY25-26.pdf", extra: { label: "Restated Financials (Signed)", href: "/investors/annual/Restated_Financials_FY25-26.pdf" } },
              { period: "FY 2026–27", href: null },
            ].map((r, i) => (
              <FadeUp key={r.period} delay={i * 0.08}>
                {r.href ? (
                  <div className="bg-card border border-border p-6 md:p-8 h-full flex flex-col justify-between min-h-[220px]" data-testid={`annual-report-${i}`}>
                    <a href={r.href} target="_blank" rel="noreferrer" className="group block" data-testid={`annual-report-${i}-primary`}>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-primary mb-4">{r.period}</p>
                      <h3 className="font-display text-2xl leading-snug group-hover:text-primary transition-colors">Annual Report</h3>
                      <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-primary inline-flex items-center gap-1.5 mt-4">Download PDF <ArrowUpRight size={12} /></span>
                    </a>
                    <div className="mt-6 flex items-center justify-between border-t border-border pt-3">
                      {r.extra ? (
                        <a href={r.extra.href} target="_blank" rel="noreferrer" className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-primary inline-flex items-center gap-1.5" data-testid={`annual-report-${i}-extra`}>
                          {r.extra.label} <ArrowUpRight size={12} />
                        </a>
                      ) : <span />}
                      <BookOpen size={16} className="text-muted-foreground" />
                    </div>
                  </div>
                ) : (
                  <div className="bg-card border border-border p-6 md:p-8 h-full flex flex-col justify-between min-h-[220px]" data-testid={`annual-report-${i}`}>
                    <div>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-primary mb-4">{r.period}</p>
                      <h3 className="font-display text-2xl leading-snug">Annual Report</h3>
                    </div>
                    <div className="mt-8 flex items-center justify-between border-t border-border pt-4">
                      <Placeholder />
                      <BookOpen size={16} className="text-muted-foreground" />
                    </div>
                  </div>
                )}
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Results */}
      <section id="financial-results" className="bg-muted border-y border-border py-20 md:py-24 scroll-mt-24" data-testid="section-financial-results">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Financial Results" title="Quarterly & half-yearly results" right={<>Results will be uploaded here as they are approved by the Board and filed with the exchanges.</>} />
          <div className="grid md:grid-cols-4 gap-4">
            {["Q1", "Q2", "Q3", "Q4"].map((q, i) => (
              <FadeUp key={q} delay={i * 0.06}>
                <div className="bg-background border border-border p-6 h-full min-h-[180px] flex flex-col justify-between" data-testid={`financial-${i}`}>
                  <div>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-primary mb-3">{q} FY 24–25</p>
                    <h3 className="font-display text-xl leading-snug">Standalone Results</h3>
                  </div>
                  <div className="mt-6 flex items-center justify-between border-t border-border pt-3">
                    <Placeholder />
                    <FileText size={14} className="text-muted-foreground" />
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Policies (15 PDFs) */}
      <section id="policies" className="py-20 md:py-24 scroll-mt-24" data-testid="section-policies">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Policies" title="Policies & governance" right={<>The following {POLICIES.length} policies have been adopted by the Board. Click any card to view or download.</>} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border" data-testid="policies-list">
            {POLICIES.map((p) => (
              <FadeUp key={p.n}>
                <a
                  href={p.href}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-card p-6 md:p-7 h-full flex items-start justify-between gap-4 group hover:bg-muted transition-colors duration-300"
                  data-testid={`policy-${p.n}`}
                >
                  <div className="flex gap-4 items-start">
                    <span className="font-mono2 text-xs text-primary shrink-0 mt-1">{p.n}</span>
                    <div>
                      <h3 className="font-display text-lg leading-snug group-hover:text-primary transition-colors">{p.title}</h3>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2 inline-flex items-center gap-1.5">
                        Download PDF <ArrowUpRight size={12} />
                      </p>
                    </div>
                  </div>
                  <Download size={16} className="text-muted-foreground shrink-0 mt-1" />
                </a>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* MOA and AOA */}
      <section id="moa-aoa" className="bg-muted border-y border-border py-20 md:py-24 scroll-mt-24" data-testid="section-moa-aoa">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="MOA & AOA" title="Constitutional documents" />
          <div className="grid md:grid-cols-2 gap-6">
            {MOA_AOA.map((it, i) => (
              <FadeUp key={it.title} delay={i * 0.08}>
                <a href={it.href} target="_blank" rel="noreferrer" className="bg-background border border-border p-8 flex items-start justify-between gap-4 group hover:bg-background/70 transition-colors" data-testid={`moa-aoa-${i}`}>
                  <div className="flex gap-4 items-start">
                    <FileCheck2 size={22} className="text-primary shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                      <h3 className="font-display text-2xl group-hover:text-primary transition-colors">{it.title}</h3>
                      <p className="text-muted-foreground mt-2 max-w-md">{it.note}</p>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-primary mt-4 inline-flex items-center gap-1.5">
                        Download PDF <ArrowUpRight size={12} />
                      </p>
                    </div>
                  </div>
                  <Download size={16} className="text-muted-foreground shrink-0 mt-1" />
                </a>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Material Contracts & Documents */}
      <section id="contracts" className="py-20 md:py-24 scroll-mt-24" data-testid="section-contracts">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHead eyebrow="Material Contracts & Documents" title="Material contracts & documents" right={<>As required under the Companies Act, 2013 and SEBI (LODR) Regulations, material documents are listed here.</>} />
          <div className="grid md:grid-cols-3 gap-6" data-testid="material-docs">
            {MATERIAL_DOCS.map((d, i) => (
              <FadeUp key={d.title} delay={i * 0.06}>
                <a href={d.href} target="_blank" rel="noreferrer" className="bg-muted border border-border p-6 md:p-8 h-full flex flex-col justify-between min-h-[220px] group hover:bg-background transition-colors" data-testid={`material-doc-${i}`}>
                  <div className="flex gap-3 items-start">
                    <Scale size={20} className="text-primary shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                      <h3 className="font-display text-lg leading-snug group-hover:text-primary transition-colors">{d.title}</h3>
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">{d.note}</p>
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-between border-t border-border pt-3">
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-primary inline-flex items-center gap-1.5">Download PDF <ArrowUpRight size={12} /></span>
                    <Download size={14} className="text-muted-foreground" />
                  </div>
                </a>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* IR Contact */}
      <section className="bg-foreground text-background py-24 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-2 gap-16">
          <FadeUp>
            <div className="flex items-center gap-3 mb-6">
              <Users size={22} className="text-primary" strokeWidth={1.5} />
              <h2 className="font-display text-3xl md:text-4xl">Shareholder Services</h2>
            </div>
            <div className="space-y-6 mt-4">
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-background/50 mb-2">Registrar & Transfer Agent</p>
                <p className="text-background/90 font-medium">MUFG Intime India Private Limited</p>
                <p className="text-background/70 text-sm leading-relaxed mt-1">C-101, Embassy 247, Lal Bahadur Shastri Marg, Vikhroli (West), Mumbai 400083</p>
                <p className="text-background/70 text-sm mt-2">
                  <a href="tel:+912249186000" className="link-underline">+91 22 4918 6000</a> · <a href="mailto:ipo.team@in.mpms.mufg.com" className="link-underline" data-testid="rta-email">ipo.team@in.mpms.mufg.com</a>
                </p>
              </div>
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-background/50 mb-2">Company Secretary & Compliance Officer</p>
                <p className="text-background/90 font-medium">Pankaj Ochani</p>
                <p className="text-background/70 text-sm mt-1">
                  <a href={`mailto:${COMPANY.email}`} className="link-underline" data-testid="co-email">{COMPANY.email}</a> · <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="link-underline">{COMPANY.phone}</a>
                </p>
              </div>
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-background/50 mb-2">Investor Grievance Redressal</p>
                <a href={`mailto:${COMPANY.email}`} className="link-underline inline-flex items-center gap-1.5" data-testid="ir-grievance-email">
                  {COMPANY.email} <ArrowUpRight size={14} />
                </a>
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="flex items-center gap-3 mb-6">
              <Mail size={22} className="text-primary" strokeWidth={1.5} />
              <h2 className="font-display text-3xl md:text-4xl">Investor Contact</h2>
            </div>
            <div className="mt-4 space-y-5">
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-background/50 mb-1">Email</p>
                <a href={`mailto:${COMPANY.email}`} className="link-underline text-lg" data-testid="ir-contact-email">{COMPANY.email}</a>
              </div>
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-background/50 mb-1">Phone</p>
                <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="link-underline text-lg" data-testid="ir-contact-phone">{COMPANY.phone}</a>
              </div>
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-background/50 mb-1">Registered Office</p>
                <p className="text-background/85 leading-relaxed max-w-sm">{COMPANY.address}</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <Footer />
    </div>
  );
}
